import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Radio, Sparkles, Loader2, RefreshCw, Merge, ArrowLeft, Clock, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

export default function LivingDoc() {
  const [meta, setMeta] = useState(null);
  const [lens, setLens] = useState("neutral");
  const [selected, setSelected] = useState(null);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [merge, setMerge] = useState(false);
  const [picks, setPicks] = useState([]);
  const [mergeOut, setMergeOut] = useState(null);
  const [merging, setMerging] = useState(false);

  useEffect(() => { api.get("/doc/topics").then(({ data }) => setMeta(data)).catch(() => {}); }, []);

  const openTopic = async (id, lensId = lens, regen = false) => {
    setSelected(id); setLoading(true); if (!regen) setDoc(null);
    try {
      const { data } = await api.get(`/doc/narrative`, { params: { topic: id, lens: lensId, regenerate: regen } });
      setDoc(data);
    } catch { setDoc(null); } finally { setLoading(false); }
  };

  const pickLens = (lid) => {
    setLens(lid);
    if (selected) openTopic(selected, lid);
  };

  const togglePick = (id) => setPicks((p) => p.includes(id) ? p.filter((x) => x !== id) : (p.length < 3 ? [...p, id] : p));
  const runMerge = async () => {
    if (picks.length < 2) return;
    setMerging(true); setMergeOut(null);
    try {
      const { data } = await api.post(`/doc/synthesis`, { topics: picks, lens });
      setMergeOut(data);
    } catch { setMergeOut({ narrative: "Couldn't synthesize right now." }); } finally { setMerging(false); }
  };

  if (!meta) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-neon-pink" /></div>;
  const gaming = meta.lenses.filter((l) => l.group === "gaming");
  const political = meta.lenses.filter((l) => l.group === "political");

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-12" data-testid="livingdoc-page">
      <Seo title="The Living Documentary of Gaming — Always Evolving" description="A living archive of gaming history and culture — arcade to esports, design to preservation — re-narrated through the lens you choose and updated as the medium moves." />

      {/* Hero */}
      <div className="mb-8">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-neon-pink/15 border border-neon-pink/50 text-neon-pink text-xs font-bold uppercase tracking-widest mb-4">
          <Radio className="w-3.5 h-3.5 animate-pulse" /> Living Documentary
        </span>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter leading-[0.95]">Gaming's story,<br /><span className="text-neon-pink">always evolving.</span></h1>
        <p className="text-zinc-400 mt-4 max-w-2xl">A living archive of gaming — history, culture, design and the moments everyone's talking about — re-narrated through the lens you choose, and updated as the medium moves.</p>
      </div>

      {/* Lens selector */}
      <div className="mb-6" data-testid="lens-bar">
        <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-2">Narrate through the lens of</p>
        <div className="flex flex-wrap gap-2">
          {[...gaming, ...political].map((l) => (
            <button key={l.id} onClick={() => pickLens(l.id)} data-testid={`doc-lens-${l.id}`}
              className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest rounded-full border-2 transition-colors ${lens === l.id ? "bg-neon-pink text-white border-neon-pink" : "border-zinc-700 text-zinc-300 hover:border-white"}`}>
              {l.label}
            </button>
          ))}
        </div>
      </div>

      {/* Merge toggle */}
      <div className="flex items-center gap-3 mb-8">
        <button onClick={() => { setMerge((m) => !m); setPicks([]); setMergeOut(null); }} data-testid="merge-toggle"
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest border-2 transition-colors ${merge ? "bg-neon-lime text-black border-neon-lime" : "border-zinc-700 text-zinc-300 hover:border-neon-lime"}`}>
          <Merge className="w-3.5 h-3.5" /> {merge ? "Merging — pick 2-3 topics" : "Synthesis · Merge thoughts"}
        </button>
        {merge && picks.length >= 2 && (
          <Button onClick={runMerge} disabled={merging} data-testid="merge-run" className="bg-neon-lime text-black rounded-none font-bold uppercase text-xs tracking-widest h-9 px-5 hover:bg-white">
            {merging ? <Loader2 className="w-4 h-4 animate-spin" /> : `Merge ${picks.length} →`}
          </Button>
        )}
      </div>

      {mergeOut && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-2xl border-2 border-neon-lime/50 bg-neon-lime/5 p-6 mb-8" data-testid="merge-output">
          <div className="flex items-center gap-2 mb-2"><Merge className="w-4 h-4 text-neon-lime" /><h3 className="font-display font-bold uppercase tracking-tight">{mergeOut.topics?.join(" × ")}</h3></div>
          <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3">through {mergeOut.lens_label}</p>
          <p className="text-zinc-200 leading-relaxed whitespace-pre-wrap">{mergeOut.narrative}</p>
        </motion.div>
      )}

      <AnimatePresence mode="wait">
        {selected ? (
          <motion.div key="narrative" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="doc-narrative">
            <button onClick={() => { setSelected(null); setDoc(null); }} className="inline-flex items-center gap-1 text-zinc-400 hover:text-neon-pink text-sm font-semibold uppercase tracking-widest mb-5">
              <ArrowLeft className="w-4 h-4" /> All topics
            </button>
            {loading || !doc ? (
              <div className="py-16 flex flex-col items-center gap-3 text-zinc-400"><Loader2 className="w-7 h-7 animate-spin text-neon-pink" /><p className="font-mono text-sm">Narrating…</p></div>
            ) : (
              <>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <h2 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">{doc.title}</h2>
                  <button onClick={() => openTopic(selected, lens, true)} data-testid="doc-regenerate" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-zinc-400 hover:text-neon-pink">
                    <RefreshCw className="w-3.5 h-3.5" /> Regenerate
                  </button>
                </div>
                <p className="text-[11px] uppercase tracking-widest text-neon-pink mt-1 flex items-center gap-2">through {doc.lens_label} <span className="text-zinc-600 flex items-center gap-1"><Clock className="w-3 h-3" /> as of {fmt(doc.as_of)}</span></p>
                <p className="text-zinc-200 leading-relaxed whitespace-pre-wrap mt-5 text-lg">{doc.narrative}</p>
                {doc.sources?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-5">
                    {doc.sources.map((s) => (<span key={s.node_id} className="text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border border-zinc-700 text-zinc-400">{s.title}</span>))}
                  </div>
                )}
                {doc.latest?.length > 0 && (
                  <div className="mt-10">
                    <div className="flex items-center gap-2 mb-4"><Sparkles className="w-4 h-4 text-neon-cyan" /><h3 className="font-display font-bold uppercase tracking-widest text-sm text-zinc-400">Where it's heading — latest coverage</h3></div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {doc.latest.map((n) => (
                        <Link key={n.id} to={`/news/story/${n.id}`} className="group flex gap-3 bg-zinc-950 border-2 border-zinc-800 hover:border-neon-cyan rounded-xl p-3 transition-colors" data-testid="doc-latest">
                          <img src={n.image} alt="" className="w-16 h-16 object-cover rounded shrink-0" />
                          <div className="min-w-0">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-neon-cyan">{n.source}</p>
                            <p className="font-display font-bold text-sm leading-tight line-clamp-3 group-hover:text-neon-cyan transition-colors">{n.title}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </motion.div>
        ) : (
          <motion.div key="grid" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="topic-grid">
            {meta.topics.map((t, i) => {
              const picked = picks.includes(t.id);
              return (
                <motion.button key={t.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 9) * 0.04 }}
                  onClick={() => merge ? togglePick(t.id) : openTopic(t.id)} data-testid={`topic-${t.id}`}
                  className={`relative text-left rounded-2xl overflow-hidden border-2 h-56 group transition-colors ${picked ? "border-neon-lime" : "border-zinc-800 hover:border-neon-pink"}`}>
                  <img src={t.image} alt={t.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                  {merge && (
                    <span className={`absolute top-3 right-3 w-6 h-6 rounded-full border-2 flex items-center justify-center ${picked ? "bg-neon-lime border-neon-lime" : "border-white/60"}`}>
                      {picked && <Check className="w-4 h-4 text-black" />}
                    </span>
                  )}
                  <div className="absolute bottom-0 p-5">
                    <h3 className="font-display font-black text-xl uppercase tracking-tight leading-tight group-hover:text-neon-pink transition-colors">{t.title}</h3>
                    <p className="text-sm text-zinc-300 mt-1 line-clamp-2">{t.blurb}</p>
                  </div>
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
